import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { ProjectIdentifier } from "../../../api/AlqemiDataService";

type CvProject = {
  title?: string;
  info?: { "Time period"?: string } & Record<string, unknown>;
  [key: string]: unknown;
};

type Row = ProjectIdentifier | null;

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (selected: ProjectIdentifier[]) => Promise<void>;
  projects: CvProject[];
  initialSelection: ProjectIdentifier[];
}

const keyOf = (id: ProjectIdentifier) => `${id.title}|${id.time_period}`;

const projectToIdentifier = (p: CvProject): ProjectIdentifier | null => {
  const title = p.title;
  const time_period = p.info?.["Time period"];
  if (!title || !time_period) return null;
  return { title, time_period };
};

export default function SimilarProjectsDialog({
  open,
  onClose,
  onSubmit,
  projects,
  initialSelection,
}: Props) {
  const [rows, setRows] = useState<Row[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Keyed on `open` only: prop updates mid-submit must not wipe selection or reset `submitting`.
  // Identifiers no longer in the CV are silently dropped (spec).
  useEffect(() => {
    if (!open) return;
    const availableKeys = new Set(
      projects.map(projectToIdentifier).filter((x): x is ProjectIdentifier => !!x).map(keyOf),
    );
    const resolved = initialSelection.filter((id) => availableKeys.has(keyOf(id)));
    setRows(resolved);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addableIdentifiers = projects
    .map(projectToIdentifier)
    .filter((x): x is ProjectIdentifier => !!x);

  const usedKeys = new Set(rows.filter((r): r is ProjectIdentifier => !!r).map(keyOf));

  const handleAddRow = () => setRows((prev) => [...prev, null]);

  const handleRemoveRow = (index: number) =>
    setRows((prev) => prev.filter((_, i) => i !== index));

  const handleRowChange = (index: number) => (event: SelectChangeEvent<string>) => {
    const key = event.target.value;
    const picked = addableIdentifiers.find((id) => keyOf(id) === key) ?? null;
    setRows((prev) => prev.map((row, i) => (i === index ? picked : row)));
  };

  const handleSubmit = async () => {
    const selected = rows.filter((r): r is ProjectIdentifier => !!r);
    setSubmitting(true);
    try {
      await onSubmit(selected);
    } catch {
      // Parent is responsible for user-visible error handling (e.g. snackbar).
      // We only need to re-enable the button via the finally block below.
    } finally {
      setSubmitting(false);
    }
  };

  const addDisabled = addableIdentifiers.length === 0 || submitting;
  const addLabel = rows.length === 0 ? "Add a project" : "Add another project";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="similar-projects-dialog-title"
      PaperProps={{ sx: { borderRadius: 0 } }}
    >
      <DialogTitle id="similar-projects-dialog-title">Have you done a similar project before?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Select any past projects similar to this job. They'll be referenced in
          the outreach email. Leave empty if none apply.
        </Typography>

        <Typography variant="overline" sx={{ color: "text.secondary" }}>
          Selected Projects
        </Typography>

        {rows.length === 0 ? (
          <Stack
            sx={{
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 0,
              p: 2,
              mt: 1,
              mb: 2,
              alignItems: "center",
              backgroundColor: "action.hover",
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
              No similar projects done in past
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
            {rows.map((row, index) => {
              const value = row ? keyOf(row) : "";
              return (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Select
                    size="small"
                    displayEmpty
                    value={value}
                    onChange={handleRowChange(index)}
                    sx={{ flex: 1, minWidth: 0, borderRadius: 0 }}
                  >
                    <MenuItem value="" disabled>
                      Select a project…
                    </MenuItem>
                    {addableIdentifiers.map((id) => {
                      const k = keyOf(id);
                      const isUsedElsewhere = usedKeys.has(k) && k !== value;
                      return (
                        <MenuItem key={k} value={k} disabled={isUsedElsewhere}>
                          {id.title} ({id.time_period})
                        </MenuItem>
                      );
                    })}
                  </Select>
                  <IconButton
                    aria-label="remove"
                    size="small"
                    onClick={() => handleRemoveRow(index)}
                    disabled={submitting}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        )}

        <Tooltip title={addableIdentifiers.length === 0 ? "No projects in your CV" : ""}>
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={addDisabled}
            >
              {addLabel}
            </Button>
          </span>
        </Tooltip>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Submit & Confirm CV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
